export const INICIO_RENDER_DEBOUNCE_MS = 120;

function toFiniteNumber(value, fallback = null) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function resolveMarkerLabel(modid, nickSnapshot) {
  const nick =
    nickSnapshot && Object.prototype.hasOwnProperty.call(nickSnapshot, modid)
      ? nickSnapshot[modid]
      : "";
  if (typeof nick === "string" && nick.trim() !== "") {
    return nick;
  }
  return String(modid);
}

function resolveMarkerFillColor(ingnition, speed) {
  if (ingnition === 0) {
    return "#FAFBFA";
  }
  if (speed === 0) {
    return "#D580D5";
  }
  return "#80C080";
}

function isInicioIgnitionOn(value) {
  return value == 1;
}

function resolveInicioStatusBucket(value) {
  if (value == 0) {
    return 0;
  }
  if (value == 1) {
    return 1;
  }
  return 2;
}

function buildVisibleInicioRow(item, sourceEntry) {
  const lastComm = item.last_comm ?? sourceEntry.last_comm;
  if (typeof lastComm === "undefined" || lastComm == null || lastComm === "") {
    return null;
  }

  return {
    ...item,
    last_comm: lastComm,
    status: item.status ?? sourceEntry.status,
    vel: item.vel ?? sourceEntry.vel,
    ingnition: item.ingnition ?? sourceEntry.ingnition,
    hdop: item.hdop ?? sourceEntry.hdop,
    rssi: item.rssi ?? sourceEntry.rssi,
    lat: item.lat ?? sourceEntry.lat,
    lon: item.lon ?? sourceEntry.lon,
  };
}

export function buildInicioDashboardState(tablePos, liveSnapshot, nickSnapshot) {
  const rows = [];
  const sourceRows = Array.isArray(tablePos) ? tablePos : [];
  const markerEntries = [];
  const movingSpeedValues = [];
  let on = 0;
  let off = 0;
  let s0 = 0;
  let s1 = 0;
  let s2 = 0;

  for (const item of sourceRows) {
    if (!item || item.modid == null) {
      continue;
    }

    const sourceEntry =
      liveSnapshot &&
      typeof liveSnapshot === "object" &&
      liveSnapshot[item.modid] &&
      typeof liveSnapshot[item.modid] === "object"
        ? liveSnapshot[item.modid]
        : item;

    const ingnition = toFiniteNumber(
      item.ingnition ?? sourceEntry.ingnition,
      0
    );
    const speed = toFiniteNumber(item.vel ?? sourceEntry.vel, 0);
    const heading = toFiniteNumber(sourceEntry.heading ?? item.heading, 0);
    const visibleRow = buildVisibleInicioRow(item, sourceEntry);

    if (visibleRow) {
      rows.push(visibleRow);

      if (isInicioIgnitionOn(visibleRow.ingnition)) {
        on++;
      } else {
        off++;
      }

      const statusBucket = resolveInicioStatusBucket(visibleRow.status);
      if (statusBucket === 0) {
        s0++;
      } else if (statusBucket === 1) {
        s1++;
      } else {
        s2++;
      }

      const visibleSpeed = toFiniteNumber(visibleRow.vel, 0);
      if (visibleSpeed > 0) {
        movingSpeedValues.push(visibleSpeed);
      }
    }

    const lat = toFiniteNumber(item.lat ?? sourceEntry.lat);
    const lng = toFiniteNumber(item.lon ?? sourceEntry.lon);
    if (lat == null || lng == null) {
      continue;
    }

    markerEntries.push({
      modid: item.modid,
      lat,
      lng,
      heading,
      speed,
      ingnition,
      title: speed + " km/h",
      labelText: resolveMarkerLabel(item.modid, nickSnapshot),
      fillColor: resolveMarkerFillColor(ingnition, speed),
    });
  }

  return {
    rows,
    markerEntries,
    movingSpeedValues,
    counts: {
      on,
      off,
      s0,
      s1,
      s2,
    },
  };
}

export function createInicioMarkerOptions(entry, map) {
  return {
    position: {
      lat: entry.lat,
      lng: entry.lng,
    },
    map,
    title: entry.title,
    icon: {
      path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
      scale: 6,
      rotation: entry.heading,
      fillColor: entry.fillColor,
      fillOpacity: 1,
      strokeWeight: 2,
      labelOrigin: new google.maps.Point(-3, -3),
    },
    label: {
      text: entry.labelText,
      color: "#212121",
      fontSize: "13px",
      fontWeight: "bold",
      className: "custom-label",
    },
  };
}

function hasInicioMarkerChanged(previousEntry, nextEntry) {
  if (!previousEntry) {
    return true;
  }

  return (
    previousEntry.lat !== nextEntry.lat ||
    previousEntry.lng !== nextEntry.lng ||
    previousEntry.heading !== nextEntry.heading ||
    previousEntry.speed !== nextEntry.speed ||
    previousEntry.ingnition !== nextEntry.ingnition ||
    previousEntry.labelText !== nextEntry.labelText
  );
}

export function reconcileInicioMarkerSnapshots(previousSnapshots, nextEntries) {
  const nextSnapshots = new Map();
  const additions = [];
  const updates = [];

  for (const entry of nextEntries) {
    if (!entry || entry.modid == null) {
      continue;
    }

    nextSnapshots.set(entry.modid, entry);
    const previousEntry = previousSnapshots.get(entry.modid);
    if (!previousEntry) {
      additions.push(entry);
      continue;
    }

    if (hasInicioMarkerChanged(previousEntry, entry)) {
      updates.push(entry);
    }
  }

  const removals = [];
  for (const [modid, previousEntry] of previousSnapshots.entries()) {
    if (!nextSnapshots.has(modid)) {
      removals.push(previousEntry);
    }
  }

  return {
    nextSnapshots,
    additions,
    updates,
    removals,
  };
}
